import FontAwesomeIcon from "app/components/layouts/Icons/FontAwesome";
import ModalDelete from 'app/components/layouts/Modals/ModalDelete';
import { toastSuccess, toastError } from 'app/utils/toastHelper';
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { deleteComment } from 'app/requests/comments';
import * as commonActions from 'app/actions/commons';
import { AppStoreState } from 'app/redux/store';
import { useTranslation } from 'next-i18next';
import { Message } from 'types/chat';
import Comment from './Comment';

interface CommentFormProps {
  onSubmit: (text: string, parentId?: number) => void;
  isReply?: boolean;
  comments?: Message[];
  fetchComments?: () => Promise<void>;
  setComments?: React.Dispatch<React.SetStateAction<Message[]>>;
}

const CommentForm: React.FC<CommentFormProps> = ({ onSubmit, isReply, comments = [], fetchComments, setComments }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [commentText, setCommentText] = useState<string>('');
  const [deleteCommentId, setDeleteCommentId] = useState<number | null>(null);
  const { auth } = useSelector((state: AppStoreState) => state.global);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current && isReply) {
      textareaRef.current.focus();
    }
  }, [isReply]);

  const handleSubmit = () => {
    if (commentText.trim()) {
      onSubmit(commentText);
      setCommentText('');
    }
  };

  const handleDelete = async () => {
    dispatch(commonActions.setLoading(true));
    try {
      await deleteComment(deleteCommentId);
      fetchComments && await fetchComments();
      toastSuccess(t('commons.success'));
    } catch (error) {
      toastError(error.message);
      console.error(error.message);
    } finally {
      dispatch(commonActions.setLoading(false));
    }
  };

  const updateComments = (newComment: Message) => {
    const updateParentComment = (parrentComment: Message, newReply: Message) => {
      parrentComment.replies = parrentComment?.replies?.map((reply: Message) => {
        return reply.id == newReply.id ? newReply : reply;
      });

      return parrentComment;
    }

    if (!!setComments) {
      setComments(
        comments.map((comment: Message) => {
          if (comment.id === newComment.parent_message_id) {
            return updateParentComment(comment, newComment);
          }

          return comment.id == newComment.id ? newComment : comment;
        })
      )
    }
  }

  const handleReply = (parentId: number, content: string) => {
    onSubmit(content, parentId);
  };

  return (
    <div>
      <ModalDelete
        title="Are you sure to delete this comment?"
        isOpen={!!deleteCommentId}
        onClose={() => setDeleteCommentId(null)}
        handleDelete={() => handleDelete()}
      />
      {auth.id &&
        <form
          className={`${isReply ? '' : 'p-3 card'}`}
        >
          <div className="d-flex align-items-center">
            <img
              src={auth?.avatar}
              className="rounded-circle mr-2"
              width={isReply ? 40 : 50}
              height={isReply ? 40 : 50}
              alt="Your avatar"
            />
            <textarea
              className="form-control"
              rows={1}
              ref={textareaRef}
              placeholder={isReply ? t('comments.reply_comment') : t('comments.enter_comment')}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              style={{
                borderRadius: '8px',
                resize: 'none',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                padding: '10px',
                fontSize: '1rem'
              }}
            />
            <button
              type="button"
              onClick={() => handleSubmit()}
              style={{ borderRadius: 7 }}
              className="btn btn-info ml-2"
            >
              <span className="d-md-block d-none">
                {isReply ? t('comments.send_reply') : t('comments.post_comment')}
              </span>
              <FontAwesomeIcon className="d-md-none" icon={faPaperPlane} />
            </button>
          </div>
        </form>
      }

      {!isReply && (
        <div className="mt-4">
          {comments.map((comment: Message) => (
            <Comment
              key={comment?.id}
              comment={comment}
              onReply={handleReply}
              updateComments={updateComments}
              handleDelete={(commentId: number) => setDeleteCommentId(commentId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentForm;
